import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Users, BellRing } from 'lucide-react';
import { Modal, Button, Select, TimeInput24 } from '../../../components';
import openShiftsService from '../../../services/carehome_manager/openShiftsService';
import { todayKey } from '../../../utils/datetime';
import { errorMessage } from './shared';

/**
 * "Offer shift to staff" — publish an extra shift from the rota.
 *
 * The carers who are FREE for it (their day off, nothing overlapping, not on
 * leave, role matching — the API decides) are told and see it under
 * Available shifts in the app. They request it; the manager approves on the
 * Available Shifts page. As the form changes, the count of free carers below
 * updates, so the manager knows before publishing whether anybody can take it.
 *
 * Props: isOpen, onClose, defaultGroupId, defaultDate ('YYYY-MM-DD'), onCreated
 */
const label = 'block text-[11px] font-extrabold uppercase tracking-wider text-[#475569] dark:text-gray-400 mb-1.5';
const input = 'w-full h-10 px-3 rounded-xl border border-[#DCE4EF] dark:border-gray-700 bg-white dark:bg-[#1f232a] dark:text-gray-200 focus:border-blue-500 outline-none text-[13.5px]';

const blank = (date, groupId) => ({
  date: date && date >= todayKey() ? date : todayKey(),
  start: '08:00',
  end: '20:00',
  group_id: groupId ? String(groupId) : '',
  job_role: '',
  places: 1,
  note: '',
  notify: true,
});

const OfferShiftModal = ({ isOpen, onClose, defaultGroupId = null, defaultDate = null, onCreated }) => {
  const [form, setForm] = useState(() => blank(defaultDate, defaultGroupId));
  const [options, setOptions] = useState({ groups: [], job_roles: [] });
  const [preview, setPreview] = useState(null); // { free, carers, names } | null
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const seq = useRef(0);

  // Fresh form each time it opens, for the team / day it was opened from.
  useEffect(() => {
    if (!isOpen) return;
    setForm(blank(defaultDate, defaultGroupId));
    setPreview(null);
    openShiftsService.options()
      .then((res) => setOptions(res?.data || { groups: [], job_roles: [] }))
      .catch(() => setOptions({ groups: [], job_roles: [] }));
  }, [isOpen, defaultDate, defaultGroupId]);

  // Who is free — re-asked (debounced) whenever the date, times or role change.
  useEffect(() => {
    if (!isOpen || !form.date || !form.start || !form.end || form.start === form.end) {
      setPreview(null);
      return undefined;
    }
    const mine = ++seq.current;
    setChecking(true);
    const timer = setTimeout(() => {
      openShiftsService.preview({ date: form.date, start: form.start, end: form.end, job_role: form.job_role })
        .then((res) => { if (mine === seq.current) setPreview(res?.data || null); })
        .catch(() => { if (mine === seq.current) setPreview(null); })
        .finally(() => { if (mine === seq.current) setChecking(false); });
    }, 350);
    return () => clearTimeout(timer);
  }, [isOpen, form.date, form.start, form.end, form.job_role]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const overnight = form.start && form.end && form.end <= form.start;

  const submit = async () => {
    if (!form.date || !form.start || !form.end) { toast.error('Choose the date and times.'); return; }
    if (form.start === form.end) { toast.error('The start and end times must be different.'); return; }
    setSaving(true);
    try {
      const res = await openShiftsService.create({
        date: form.date,
        start: form.start,
        end: form.end,
        group_id: form.group_id ? Number(form.group_id) : null,
        job_role: form.job_role || null,
        places: Number(form.places) || 1,
        note: form.note.trim() || null,
        notify: form.notify,
      });
      toast.success(res?.message || 'Shift published.');
      onCreated?.(res?.data);
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not publish the shift.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={saving ? undefined : onClose} title="Offer shift to staff" maxWidth="max-w-[560px]">
      <div className="space-y-4 pt-1">
        <p className="text-[12px] text-[#64748B] dark:text-gray-400 -mt-1">
          Every carer who is free for this shift is told and can request it in the app. You choose who gets it.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={label} htmlFor="offer-date">Date</label>
            <input
              id="offer-date"
              type="date"
              min={todayKey()}
              value={form.date}
              onChange={(e) => set('date')(e.target.value)}
              className={input}
            />
          </div>
          <div>
            <span className={label}>Start</span>
            <TimeInput24 value={form.start} onChange={set('start')} label="Start" />
          </div>
          <div>
            <span className={label}>End</span>
            <TimeInput24 value={form.end} onChange={set('end')} label="End" />
          </div>
        </div>
        {overnight && (
          <p className="-mt-2 text-[11px] font-semibold text-[#64748B] dark:text-gray-400">Overnight — ends the next morning.</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <span className={label}>Team</span>
            <Select
              className="w-full"
              value={form.group_id}
              onChange={set('group_id')}
              options={[{ value: '', label: 'No team' }, ...options.groups.map((g) => ({ value: String(g.id), label: g.name }))]}
            />
          </div>
          <div>
            <span className={label}>Role</span>
            <Select
              className="w-full"
              value={form.job_role}
              onChange={set('job_role')}
              options={[{ value: '', label: 'Any carer' }, ...options.job_roles.map((r) => ({ value: r, label: r }))]}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-4">
          <div>
            <label className={label} htmlFor="offer-places">Places</label>
            <input
              id="offer-places"
              type="number"
              min={1}
              max={10}
              value={form.places}
              onChange={(e) => set('places')(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
              className={input}
            />
          </div>
          <div>
            <label className={label} htmlFor="offer-note">Note (optional)</label>
            <input
              id="offer-note"
              type="text"
              value={form.note}
              maxLength={1000}
              onChange={(e) => set('note')(e.target.value)}
              placeholder="e.g. covering sickness on the first floor"
              className={`${input} dark:placeholder:text-gray-500`}
            />
          </div>
        </div>

        {/* Who is free for it right now */}
        <div className="flex items-start gap-3 px-3 py-2.5 rounded-xl bg-[#F2F6FC] dark:bg-[#31373f]">
          <Users className="w-4 h-4 mt-0.5 shrink-0 text-blue-600 dark:text-blue-300" />
          <div className="min-w-0 text-[12px] text-[#3b4a61] dark:text-gray-200">
            {checking && !preview ? (
              <span className="text-[#64748B] dark:text-gray-400">Checking who is free…</span>
            ) : preview ? (
              <>
                <strong>{preview.free} of {preview.carers} carer{preview.carers === 1 ? '' : 's'} free</strong>
                <span className="text-[#64748B] dark:text-gray-400"> — day off, no overlapping shift, not on leave{form.job_role ? `, ${form.job_role}` : ''}.</span>
                {preview.names?.length > 0 && (
                  <div className="mt-1 text-[11px] text-[#64748B] dark:text-gray-400 truncate" title={preview.names.join(', ')}>
                    {preview.names.join(', ')}{preview.free > preview.names.length ? ` and ${preview.free - preview.names.length} more` : ''}
                  </div>
                )}
              </>
            ) : (
              <span className="text-[#64748B] dark:text-gray-400">Choose a date and times to see who is free.</span>
            )}
          </div>
        </div>

        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={form.notify}
            onChange={(e) => set('notify')(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <BellRing className="w-4 h-4 text-[#64748B] dark:text-gray-400" />
          <span className="text-[12.5px] font-semibold text-[#334155] dark:text-gray-200">Notify eligible staff now</span>
        </label>

        <div className="flex items-center justify-end gap-2.5 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving}>Publish shift</Button>
        </div>
      </div>
    </Modal>
  );
};

export default OfferShiftModal;
